import React, { useState, useEffect, useRef } from "react";
import { motion} from "framer-motion";
import { MapPin, X, Info, Globe, Search, ChevronDown } from "lucide-react";
import JurisdictionList from "../JurisdictionList/JurisdictionList";
import styles from "./WorkZoneSelectorNew.module.scss";

const variants = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -10 }
};

interface WorkZone {
  jurisdiccion: { id: string; name: string };
  name: string;
  city: string;
}

interface WorkZoneSelectorNewProps {
  workZoneType: 'allCountry' | 'specificZones';
  onWorkZoneTypeChange: (type: 'allCountry' | 'specificZones') => void;
  jobBoardZones: WorkZone[];
  onJobBoardZonesChange: (zones: WorkZone[]) => void;
  getCities: (jurisdictionId: string) => void;
  cities: any[];
}

const WorkZoneSelectorNew: React.FC<WorkZoneSelectorNewProps> = ({
  workZoneType,
  onWorkZoneTypeChange,
  jobBoardZones,
  onJobBoardZonesChange,
  getCities,
  cities
}) => {
  const [showJurisdictionList, setShowJurisdictionList] = useState(false);
  const [showCityList, setShowCityList] = useState(false);
  const [showCitySuggestions, setShowCitySuggestions] = useState(false);
  const [cityInputValue, setCityInputValue] = useState('');
  const [selectedJurisdiction, setSelectedJurisdiction] = useState<{ id: string; name: string } | null>(null);

  // Referencias para detectar clicks fuera de los dropdowns
  const jurisdictionRef = useRef<HTMLDivElement>(null);
  const cityRef = useRef<HTMLDivElement>(null);


  useEffect(() => {

    if (jobBoardZones.length > 0) {
      const uniqueZones = removeDuplicateZones(jobBoardZones);
      if (uniqueZones.length !== jobBoardZones.length) {
        onJobBoardZonesChange(uniqueZones);
      }
    }
    
    if (workZoneType === 'specificZones' && jobBoardZones.length === 0) {
      onWorkZoneTypeChange('allCountry');
    }
  }, []); 

  // Función para remover duplicados de zonas
  const removeDuplicateZones = (zones: WorkZone[]): WorkZone[] => {
    const seen = new Set();
    return zones.filter(zone => {
      const key = `${zone.jurisdiccion.id}-${zone.city}`;
      if (seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });
  };

  useEffect(() => {
    if (selectedJurisdiction) {
      getCities(selectedJurisdiction.id);
    }
  }, [selectedJurisdiction, getCities]);

  // Detectar clicks fuera de los dropdowns para cerrarlos
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Cerrar dropdown de jurisdicción si se hace click fuera
      if (jurisdictionRef.current && !jurisdictionRef.current.contains(event.target as Node)) {
        setShowJurisdictionList(false);
      }
      
      // Cerrar dropdown de ciudad si se hace click fuera
      if (cityRef.current && !cityRef.current.contains(event.target as Node)) {
        setShowCityList(false);
        setShowCitySuggestions(false);
      }
    };

    // Agregar event listener
    document.addEventListener('mousedown', handleClickOutside);
    
    // Cleanup
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleAddWorkZone = (city: any) => {
    if (!selectedJurisdiction) return;

    const newWorkZone: WorkZone = {
      jurisdiccion: { id: selectedJurisdiction.id, name: selectedJurisdiction.name },
      name: selectedJurisdiction.name,
      city: city.name
    };

    // Verificar si ya existe una zona con la misma jurisdicción y ciudad
    const isDuplicate = jobBoardZones.some(zone => 
      zone.jurisdiccion.id === newWorkZone.jurisdiccion.id && 
      zone.city === newWorkZone.city
    );

    if (isDuplicate) {
      return;
    }

    onJobBoardZonesChange([...jobBoardZones, newWorkZone]);
    
    // Reset form
    setSelectedJurisdiction(null);
    setCityInputValue('');
    setShowCityList(false);
    setShowCitySuggestions(false);
  };

  const handleRemoveWorkZone = (index: number) => {
    const newZones = jobBoardZones.filter((_, i) => i !== index);
    onJobBoardZonesChange(newZones);
  };

  const handleCitySuggestions = (value: string) => {
    setCityInputValue(value);
    if (value.length < 3) {
      setShowCitySuggestions(false);
      setShowCityList(false);
    } else {
      setShowCitySuggestions(true);
      setShowCityList(false);
    }
  };

  const matchingCities = cities?.filter((city) => 
    city.name.toLowerCase().includes(cityInputValue.toLowerCase())
  ) || [];

  return (
    <motion.div
      className={styles.container}
      initial="hidden"
      animate="visible"
      variants={variants}
      transition={{ duration: 0.3 }}
    >
      <h4 className={styles.title}>Lugares donde trabajas</h4>
      
      {/* Radio Button Options */}
      <div className={styles.optionsContainer}>
        <div className={styles.option}>
          <input
            type="radio"
            id="allCountry"
            name="workZoneType"
            value="allCountry"
            checked={workZoneType === 'allCountry'}
            onChange={() => {
              onWorkZoneTypeChange('allCountry');
            }}
            className={styles.radioInput}
          />
          <label htmlFor="allCountry" className={styles.optionLabel}>
            <Globe size={20} className={styles.optionIcon} />
            <span>Vendo productos y/o servicios en todo el país</span>
          </label>
        </div>

        <div className={styles.option}>
          <input
            type="radio"
            id="specificZones"
            name="workZoneType"
            value="specificZones"
            checked={workZoneType === 'specificZones'}
            onChange={() => {
              onWorkZoneTypeChange('specificZones');
            }}
            className={styles.radioInput}
          />
          <label htmlFor="specificZones" className={styles.optionLabel}>
            <MapPin size={20} className={styles.optionIcon} />
            <span>Seleccionar zonas específicas</span>
          </label>
        </div>
      </div>

      {/* Zone Selector - Solo visible si se selecciona 'specificZones' */}
      {workZoneType === 'specificZones' && (
        <motion.div
          className={styles.zoneSelector}
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          transition={{ duration: 0.3 }}
        >
          <div className={styles.zoneSelectorHeader}>
            <h5>Selecciona las zonas donde trabajas</h5>
            <p>Agrega las jurisdicciones y ciudades donde desarrollas tu actividad</p>
          </div>

          <div className={styles.inputsContainer}>
            {/* Jurisdicción */}
            <div className={styles.inputGroup}>
              <label className={styles.inputLabel}>Jurisdicción</label>
              <div className={styles.selectContainer} ref={jurisdictionRef}>
                <button
                  type="button"
                  className={styles.selectButton}
                  onClick={() => setShowJurisdictionList(!showJurisdictionList)}
                >
                  <input
                    type="text"
                    placeholder="Seleccionar jurisdicción"
                    value={selectedJurisdiction ? selectedJurisdiction.name : ''}
                    readOnly
                    className={styles.selectInput}
                  />
                  <ChevronDown 
                    size={16} 
                    className={`${styles.arrowIcon} ${showJurisdictionList ? styles.arrowUp : ''}`} 
                  />
                </button>

                {showJurisdictionList && (
                  <div className={styles.dropdownList}>
                    {JurisdictionList.map((jurisdiction) => (
                      <div
                        key={jurisdiction.id}
                        className={styles.dropdownItem}
                        onClick={() => {
                          setSelectedJurisdiction({ id: jurisdiction.id.toString(), name: jurisdiction.name });
                          setShowJurisdictionList(false);
                        }}
                      >
                        {jurisdiction.name}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Ciudad */}
            <div className={`${styles.inputGroup} ${!selectedJurisdiction ? styles.disabled : ''}`}>
              <label className={styles.inputLabel}>Ciudad</label>
              <div className={styles.selectContainer} ref={cityRef}>
                <button
                  type="button"
                  className={`${styles.selectButton} ${!selectedJurisdiction ? styles.disabled : ''}`}
                  onClick={() => !selectedJurisdiction ? null : setShowCityList(!showCityList)}
                  disabled={!selectedJurisdiction}
                >
                  <input
                    type="text"
                    placeholder="Buscar ciudad"
                    value={cityInputValue}
                    onChange={(e) => handleCitySuggestions(e.target.value)}
                    className={`${styles.selectInput} ${!selectedJurisdiction ? styles.disabled : ''}`}
                    disabled={!selectedJurisdiction}
                  />
                  <Search size={16} className={styles.searchIcon} />
                </button>

                {showCitySuggestions && cityInputValue.length >= 3 && (
                  <div className={styles.dropdownList}>
                    {matchingCities.map((city, index) => (
                      <div
                        key={index}
                        className={styles.dropdownItem}
                        onClick={() => handleAddWorkZone(city)}
                      >
                        {city.name}
                      </div>
                    ))}
                  </div>
                )}

                {showCityList && !showCitySuggestions && (
                  <div className={styles.dropdownList}>
                    {cities?.map((city, index) => (
                      <div
                        key={index}
                        className={styles.dropdownItem}
                        onClick={() => handleAddWorkZone(city)}
                      >
                        {city.name}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Zonas seleccionadas */}
          <div className={styles.selectedZones}>
            <h6>Zonas seleccionadas ({jobBoardZones.length})</h6>
            
            {jobBoardZones.length > 0 ? (
              <div className={styles.zonesList}>
                {jobBoardZones.map((zone, index) => (
                  <div
                    key={index}
                    className={styles.zoneItem}
                    onClick={() => handleRemoveWorkZone(index)}
                  >
                    <MapPin size={16} className={styles.zoneIcon} />
                    <span>{zone.name} - {zone.city}</span>
                    <X size={16} className={styles.removeIcon} />
                  </div>
                ))}
              </div>
            ) : (
              <div className={styles.emptyState}>
                <Info size={16} className={styles.infoIcon} />
                <span>No has seleccionado ninguna zona aún</span>
              </div>
            )}
          </div>
        </motion.div>
      )}

      {/* Mensaje de validación */}
      {workZoneType === 'specificZones' && jobBoardZones.length === 0 && (
        <div className={styles.validationMessage}>
          <Info size={16} />
          <span>Debes seleccionar al menos una zona o marcar "Vendo productos y/o servicios en todo el país"</span>
        </div>
      )}
    </motion.div>
  );
};

export default WorkZoneSelectorNew;
